import React, { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import PageHeader from '../../../components/PageHeader';
import { getSecFilingSnapshots } from '../../../api';
import useSecFilingBootstrap from '../hooks/useSecFilingBootstrap';
import { assignFilterArray } from '../../../utils/filtersApi';
import SecFilingDataTableHead from '../components/SecFilingDataTableHead';
import SecFilingDetailDrawer from '../components/SecFilingDetailDrawer';
import SecFilingFiltersDrawer from '../components/SecFilingFiltersDrawer';
import SecFilingTableRow from '../components/SecFilingTableRow';
import useSecFilingFilters from '../hooks/useSecFilingFilters';
import { resolveSecPeriods } from '../utils/secFilingConstants';
import { SEC_FILTER_DEFAULTS } from '../utils/secFilingFilters';

export default function SecFilingTable() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { meta, filterOptions } = useSecFilingBootstrap();
  const filters = useSecFilingFilters({
    syncSearchParams: true,
    searchParams,
    setSearchParams,
    filterOptions,
  });
  const {
    filterCompany,
    filterFormType,
    filterXbrlTag,
    filedStart,
    filedEnd,
    showTableFilters,
    setShowTableFilters,
    hasFiltersApplied,
    handleClearFilters,
  } = filters;

  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [pageInput, setPageInput] = useState('1');
  const [selectedId, setSelectedId] = useState(null);
  const [openFilter, setOpenFilter] = useState(null);
  const [rows, setRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(true);
  const [drawerFiling, setDrawerFiling] = useState(null);

  const periods = useMemo(() => resolveSecPeriods(meta), [meta]);

  const filterPeriod = useMemo(() => {
    const periodKey = searchParams.get('period');
    return periods.find((period) => period.key === periodKey) || null;
  }, [searchParams, periods]);

  useEffect(() => {
    if (searchParams.get('filing')) return;
    setCurrentPage(1);
  }, [
    searchParams.get('company'),
    searchParams.get('period'),
    searchParams.get('from'),
    searchParams.get('to'),
  ]);

  useEffect(() => {
    if (!filterOptions) return undefined;
    let cancelled = false;

    const fetchSnapshots = async () => {
      setLoading(true);
      try {
        const body = {
          page: currentPage,
          limit: rowsPerPage,
          search: searchTerm.trim() || undefined,
        };
        if (filterPeriod?.key) {
          body.periodKey = filterPeriod.key;
        }
        assignFilterArray(body, 'company', filterCompany, SEC_FILTER_DEFAULTS.company, filterOptions.companies);
        assignFilterArray(body, 'filingType', filterFormType, SEC_FILTER_DEFAULTS.type, filterOptions.formTypes);
        assignFilterArray(body, 'xbrlTag', filterXbrlTag, SEC_FILTER_DEFAULTS.xbrlTag, filterOptions.xbrlTags);
        if (filedStart) body.filedFrom = format(filedStart, 'yyyy-MM-dd');
        if (filedEnd) body.filedTo = format(filedEnd, 'yyyy-MM-dd');

        const response = await getSecFilingSnapshots(body);
        if (!cancelled) {
          setRows(response.data?.data || []);
          setTotalRows(response.data?.meta?.totalItems || 0);
        }
      } catch (error) {
        console.error('Error fetching SEC filings:', error);
        if (!cancelled) {
          setRows([]);
          setTotalRows(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    const timer = setTimeout(fetchSnapshots, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    filterOptions,
    currentPage,
    rowsPerPage,
    searchTerm,
    filterCompany,
    filterFormType,
    filterXbrlTag,
    filedStart,
    filedEnd,
    filterPeriod,
  ]);

  const totalPages = Math.max(1, Math.ceil(totalRows / rowsPerPage));
  const safePage = Math.min(currentPage, totalPages);
  const displayedRows = rows;

  const selectedFiling = useMemo(
    () => drawerFiling
      || displayedRows.find((row) => row.id === selectedId)
      || null,
    [displayedRows, selectedId, drawerFiling],
  );

  useEffect(() => {
    const filingId = searchParams.get('filing');
    if (!filingId) {
      setDrawerFiling(null);
      return undefined;
    }

    const rowInPage = displayedRows.find((row) => row.id === filingId);
    if (rowInPage) {
      setSelectedId(filingId);
      setDrawerFiling(rowInPage);
      return undefined;
    }

    let cancelled = false;
    const loadFiling = async () => {
      try {
        const response = await getSecFilingSnapshots({ id: filingId });
        const row = response.data?.data;
        if (!cancelled && row) {
          setSelectedId(filingId);
          setDrawerFiling(row);
        }
      } catch (error) {
        console.error('Error loading SEC filing detail:', error);
      }
    };
    loadFiling();
    return () => { cancelled = true; };
  }, [searchParams, displayedRows]);

  const handleSelectFiling = (filingId) => {
    setSelectedId(filingId);
    const next = new URLSearchParams(searchParams);
    if (filingId) next.set('filing', filingId);
    else next.delete('filing');
    setSearchParams(next, { replace: true });
  };

  const handleCloseDrawer = () => {
    setSelectedId(null);
    setDrawerFiling(null);
    if (searchParams.get('filing')) {
      const next = new URLSearchParams(searchParams);
      next.delete('filing');
      setSearchParams(next, { replace: true });
    }
  };

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
    setPageInput(String(Math.min(currentPage, totalPages)));
  }, [currentPage, totalPages]);

  const handlePageInputSubmit = (event) => {
    event.preventDefault();
    const next = parseInt(pageInput, 10);
    if (!Number.isNaN(next) && next >= 1 && next <= totalPages) {
      setCurrentPage(next);
    } else {
      setPageInput(String(safePage));
    }
  };

  return (
    <>
      <PageHeader
        title="SEC Filing"
        activePageTab="Table"
        searchTerm={searchTerm}
        onSearchChange={(e) => {
          setSearchTerm(e.target.value);
          setCurrentPage(1);
        }}
        placeholder="Search company or filing type..."
        hasFiltersApplied={hasFiltersApplied}
        onClearFilters={() => {
          handleClearFilters();
          setCurrentPage(1);
        }}
        showTableFilters={showTableFilters}
        onToggleFilters={() => setShowTableFilters((open) => !open)}
      />

      <div className="body">
        <div className="table-area sec-filing-panel filings-table-panel">
          <div
            key={`sec-table-${safePage}-${rowsPerPage}-${totalRows}-${filterCompany}-${filterFormType}-${filterXbrlTag}-${filterPeriod?.key || ''}`}
            className="table-wrap"
          >
            <table className="sec-filing-insights-data-table">
              <colgroup>
                <col className="col-sno" />
                <col className="col-company" />
                <col className="col-entity" />
                <col className="col-type" />
                <col className="col-priority" />
                <col className="col-accepted" />
                <col className="col-date" />
                <col className="col-file-film" />
              </colgroup>
              <SecFilingDataTableHead />
              <tbody>
                {loading && displayedRows.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="sec-filing-insights-period-table__empty">
                      Loading SEC filings…
                    </td>
                  </tr>
                ) : displayedRows.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="sec-filing-insights-period-table__empty">
                      No SEC filings match your search or filters.
                    </td>
                  </tr>
                ) : (
                  displayedRows.map((row, rowIdx) => (
                    <SecFilingTableRow
                      key={row.id}
                      row={row}
                      rowIndex={rowIdx}
                      startIndex={(totalRows === 0 ? 0 : (safePage - 1) * rowsPerPage)}
                      isSelected={selectedId === row.id}
                      subjectCompany={meta?.subjectCompany || ''}
                      onSelect={handleSelectFiling}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="pagination pagination-sticky">
            <div className="pg-info">
              Showing {totalRows === 0 ? 0 : (safePage - 1) * rowsPerPage + 1}–{Math.min(safePage * rowsPerPage, totalRows)} of {totalRows} filings
            </div>
            <div className="table-pagination">
              <div className="table-pagination__control">
                <span className="table-pagination__label">Rows per page</span>
                <div className={`select-wrap ${openFilter === 'rows' ? 'is-open' : ''}`}>
                  <span className="select-measure table-pagination__measure">{rowsPerPage}</span>
                  <select
                    className="filter-select table-pagination__select"
                    value={rowsPerPage}
                    onFocus={() => setOpenFilter('rows')}
                    onBlur={() => setOpenFilter(null)}
                    onChange={(e) => {
                      setRowsPerPage(parseInt(e.target.value, 10));
                      setCurrentPage(1);
                      e.target.blur();
                    }}
                  >
                    <option value={25}>25</option>
                    <option value={35}>35</option>
                    <option value={50}>50</option>
                  </select>
                </div>
              </div>
              <div className="pg-controls">
                <button
                  type="button"
                  className="pg-btn"
                  disabled={safePage === 1}
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                >
                  <ChevronLeft size={14} />
                </button>
                <form
                  onSubmit={handlePageInputSubmit}
                  className="companydetails-style-33"
                >
                  <input
                    type="text"
                    className="pg-input"
                    value={pageInput}
                    onChange={(e) => setPageInput(e.target.value)}
                    onBlur={() => setPageInput(String(safePage))}
                  />
                  <button type="submit" style={{ display: 'none' }} />
                </form>
                <button
                  type="button"
                  className="pg-btn"
                  disabled={safePage === totalPages || totalRows === 0}
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                >
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>
          </div>
        </div>

        <SecFilingDetailDrawer
          filing={selectedFiling}
          onClose={handleCloseDrawer}
        />
      </div>

      <SecFilingFiltersDrawer
        isOpen={showTableFilters}
        onClose={() => setShowTableFilters(false)}
        filters={filters}
        setCurrentPage={setCurrentPage}
      />
    </>
  );
}
